/**
 * Service layer for the full-catalog export. Plain async fetchers over
 * `exportClient` returning the contract DTOs verbatim. Tracking, history and
 * download reuse `../../services/export.service` — a catalog export is an
 * ordinary export row.
 */

import { exportClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type {
  ExportConnector,
  ExportEstimateResponse,
  ExportTriggerResponse,
} from '../../types/export.types';
import type {
  CatalogValidationTargets,
  CatalogBatchPreset,
  CatalogBatchesResponse,
  CatalogBrowsePage,
  CatalogBrowseRequest,
  CatalogVersionsRequest,
  CatalogVersionsResponse,
  CatalogExportRequest,
  CatalogFacetsRequest,
  CatalogFacetsResponse,
  CatalogPreview,
  CatalogSummary,
  CatalogTriggerRequest,
} from '../types/catalog-export.types';

/** The batch picker: every batch of the connector, marked with whether the preset takes it. */
export async function getCatalogBatches(
  connector: ExportConnector,
  preset: CatalogBatchPreset,
  includeArchived: boolean,
): Promise<CatalogBatchesResponse> {
  return exportClient
    .get(endpoints.catalogExports.batches(), {
      searchParams: { connector, preset, includeArchived },
    })
    .json<CatalogBatchesResponse>();
}

/** The metrics bar: SKUs, products, duplicates collapsed, fall-backs, markets, freshness. */
export async function getCatalogSummary(request: CatalogExportRequest): Promise<CatalogSummary> {
  return exportClient
    .post(endpoints.catalogExports.summary(), { json: request })
    .json<CatalogSummary>();
}

/** Dry-run: file rows + measured size for the selection, no workflow started. */
export async function estimateCatalogExport(
  request: CatalogExportRequest,
): Promise<ExportEstimateResponse> {
  return exportClient
    .post(endpoints.catalogExports.estimate(), { json: request })
    .json<ExportEstimateResponse>();
}

/** The first SKUs (browse order) rendered raw + encoded, each with its source batch. */
export async function previewCatalogExport(
  request: CatalogExportRequest,
  rows: number,
): Promise<CatalogPreview> {
  return exportClient
    .post(endpoints.catalogExports.preview(), { json: { ...request, rows } })
    .json<CatalogPreview>();
}

/** One keyset page of the catalog (by SKU, or whole products), rendered as the file would be. */
export async function browseCatalog(request: CatalogBrowseRequest): Promise<CatalogBrowsePage> {
  return exportClient
    .post(endpoints.catalogExports.browse(), { json: request })
    .json<CatalogBrowsePage>();
}

/** Every version of the given SKUs / products in the selected batches, failures included. */
export async function getCatalogVersions(
  request: CatalogVersionsRequest,
): Promise<CatalogVersionsResponse> {
  return exportClient
    .post(endpoints.catalogExports.versions(), { json: request })
    .json<CatalogVersionsResponse>();
}

/** Distinct shipped values (with counts) of some columns, for the grid's dropdown filters. */
export async function getCatalogFacets(
  request: CatalogFacetsRequest,
): Promise<CatalogFacetsResponse> {
  return exportClient
    .post(endpoints.catalogExports.facets(), { json: request })
    .json<CatalogFacetsResponse>();
}

/** The rows the file ships (selection + grid narrowing), grouped by batch, for validation. */
export async function getCatalogValidationTargets(
  request: CatalogExportRequest,
): Promise<CatalogValidationTargets> {
  return exportClient
    .post(endpoints.catalogExports.validationTargets(), { json: request })
    .json<CatalogValidationTargets>();
}

/** Start a catalog export (202). The selection is frozen server-side at this moment. */
export async function triggerCatalogExport(
  request: CatalogTriggerRequest,
): Promise<ExportTriggerResponse> {
  return exportClient
    .post(endpoints.catalogExports.trigger(), { json: request })
    .json<ExportTriggerResponse>();
}
